import { logger } from '../utils/logger';

export interface DeferredWeakArea {
  testId: string;
  testName?: string;
  score: number;
  dateDetected: string;
  retryCount: number;
}

const STORAGE_KEY = 'deferredWeakAreaQueue';

function safeParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

function loadQueue(): DeferredWeakArea[] {
  const raw = (globalThis as any).localStorage?.getItem?.(STORAGE_KEY) ?? null;
  const data = safeParse<DeferredWeakArea[]>(raw, []);
  return Array.isArray(data) ? data : [];
}

function saveQueue(queue: DeferredWeakArea[]) {
  try {
    (globalThis as any).localStorage?.setItem?.(STORAGE_KEY, JSON.stringify(queue));
  } catch (e) {
    logger.debug('deferredWeakAreaQueue: failed to persist', e);
  }
}

export const deferredWeakAreaQueue = {
  getQueuedWeakAreas(): DeferredWeakArea[] {
    return loadQueue();
  },

  getNextWeakArea(): DeferredWeakArea | null {
    const q = loadQueue();
    return q.length > 0 ? q[0] : null;
  },

  isInQueue(testId: string): boolean {
    const q = loadQueue();
    return q.some((a) => a.testId === testId);
  },

  getWeakArea(testId: string): DeferredWeakArea | null {
    const q = loadQueue();
    return q.find((a) => a.testId === testId) ?? null;
  },

  addWeakArea(testId: string, score: number, testName?: string) {
    const q = loadQueue();
    const now = new Date().toISOString();
    const existing = q.find((a) => a.testId === testId);
    if (existing) {
      existing.score = Math.min(existing.score, score);
      existing.testName = existing.testName || testName;
      existing.dateDetected = existing.dateDetected || now;
    } else {
      q.push({
        testId,
        testName,
        score,
        dateDetected: now,
        retryCount: 0,
      });
    }
    saveQueue(q);
  },

  /**
   * Update weak area after practice. Returns true if it was mastered (>=60) and removed.
   */
  updateWeakAreaAfterPractice(testId: string, score: number): boolean {
    const q = loadQueue();
    const idx = q.findIndex((a) => a.testId === testId);
    if (idx === -1) return false;
    q[idx].retryCount = (q[idx].retryCount || 0) + 1;
    q[idx].score = score;

    const mastered = score >= 60;
    if (mastered) {
      q.splice(idx, 1);
    }
    saveQueue(q);
    return mastered;
  },

  removeWeakArea(testId: string) {
    const q = loadQueue().filter((a) => a.testId !== testId);
    saveQueue(q);
  },

  clear() {
    saveQueue([]);
  },
};

